import { useHotkeys } from 'react-hotkeys-hook';

interface EditorShortcutsProps {
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onNavigateNext: () => void;
  onNavigatePrev: () => void;
  canUndo: boolean;
  canRedo: boolean;
  canNavigateNext: boolean;
  canNavigatePrev: boolean;
  canSave: boolean;
  enabled?: boolean;
}

/**
 * Simple keyboard shortcuts hook for the proposal editor
 * Focuses on essential shortcuts only to avoid over-engineering
 */
export const useEditorShortcuts = ({
  onSave,
  onUndo,
  onRedo,
  onNavigateNext,
  onNavigatePrev,
  canUndo,
  canRedo,
  canNavigateNext,
  canNavigatePrev,
  canSave,
  enabled = true
}: EditorShortcutsProps) => {
  
  // Save shortcut - Ctrl+S (or Cmd+S on Mac)
  useHotkeys(
    'ctrl+s, meta+s',
    (event) => {
      event.preventDefault();
      if (canSave) {
        onSave();
      }
    },
    { enabled },
    [onSave, canSave]
  );

  // Undo shortcut - Ctrl+Z (or Cmd+Z on Mac)
  useHotkeys(
    'ctrl+z, meta+z',
    (event) => {
      event.preventDefault();
      if (canUndo) {
        onUndo();
      }
    },
    { enabled },
    [onUndo, canUndo]
  );

  // Redo shortcut - Ctrl+Y or Ctrl+Shift+Z (or Cmd equivalents on Mac)
  useHotkeys(
    'ctrl+y, meta+y, ctrl+shift+z, meta+shift+z',
    (event) => {
      event.preventDefault();
      if (canRedo) {
        onRedo();
      }
    },
    { enabled },
    [onRedo, canRedo]
  );

  // Navigate to next profile - Ctrl+Right Arrow (or Cmd+Right on Mac)
  useHotkeys(
    'ctrl+ArrowRight, meta+ArrowRight',
    (event) => {
      event.preventDefault();
      if (canNavigateNext) {
        onNavigateNext();
      }
    },
    { enabled },
    [onNavigateNext, canNavigateNext]
  );

  // Navigate to previous profile - Ctrl+Left Arrow (or Cmd+Left on Mac)
  useHotkeys(
    'ctrl+ArrowLeft, meta+ArrowLeft',
    (event) => {
      event.preventDefault();
      if (canNavigatePrev) {
        onNavigatePrev();
      }
    },
    { enabled },
    [onNavigatePrev, canNavigatePrev]
  );

  // Return shortcuts info for help display
  return {
    shortcuts: {
      save: 'Ctrl+S',
      undo: 'Ctrl+Z',
      redo: 'Ctrl+Y',
      navigateNext: 'Ctrl+→',
      navigatePrev: 'Ctrl+←'
    },
    enabled
  };
};

export default useEditorShortcuts;